// ===== FILE: src/hooks/useContractors.ts =====
import { useState, useEffect, useCallback } from 'react';
import { contractorAPI, ContractorFilters } from '../services/contractorService';
import { Contractor } from '../models/Contractor.interface';

interface UseContractorsState {
  contractors: Contractor[];
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
  };
}

interface UseContractorsReturn extends UseContractorsState {
  refetch: () => void;
  setPage: (page: number) => void;
  updateFilters: (newFilters: Partial<ContractorFilters>) => void;
}

export const useContractors = (
  initialFilters: ContractorFilters = {},
  autoFetch = true
): UseContractorsReturn => {
  const [state, setState] = useState<UseContractorsState>({
    contractors: [],
    loading: false,
    error: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      limit: 12
    }
  });

  const [filters, setFilters] = useState<ContractorFilters>(initialFilters);

  const fetchContractors = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await contractorAPI.getContractors(filters);
      
      setState(prev => ({
        ...prev,
        contractors: response.data,
        pagination: response.pagination,
        loading: false
      }));
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to fetch contractors',
        loading: false
      }));
    }
  }, [filters]);

  const refetch = useCallback(() => {
    fetchContractors();
  }, [fetchContractors]);

  const setPage = useCallback((page: number) => {
    setFilters(prev => ({ ...prev, page }));
  }, []);

  const updateFilters = useCallback((newFilters: Partial<ContractorFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 })); // Reset to page 1 when filters change
  }, []);

  useEffect(() => {
    if (autoFetch) {
      fetchContractors();
    }
  }, [fetchContractors, autoFetch]);

  return {
    ...state,
    refetch,
    setPage,
    updateFilters
  };
};

